// src/app/router.tsx
import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { NavigationGuard } from '@/features/shared/components/recovery/NavigationGuard';
import { Splash } from '@/features/shared/components/ui/Splash';
import { RouteErrorFallback } from '@/features/shared/components/PageBoundary';

// Lazy load pages for code splitting
// NOTE: the marketing/landing ("welcome") page now lives in a separate
// advertisement app. This app defaults straight to /login.
const WorkspaceEntry = lazy(() => import('@/features/auth/entry-points/components/WorkspaceEntry'));
const TestHeader = lazy(() => import('@/components/TestHeader'));
const GuarantorSignPage = lazy(() => import('@/features/pages/public/GuarantorSignPage'));
const WorkAckPage = lazy(() => import('@/features/pages/public/WorkAckPage'));
const VerifyDocumentPage = lazy(() => import('@/features/pages/public/VerifyDocumentPage'));

// Use our new designed components
const LoginPage = lazy(() => import('@/components/auth/MultiTenantSignIn'));
const InviteOnlySignup = lazy(() => import('@/components/auth/InviteOnlySignup'));

// Gated company registration (replaces the old public self-service wizard):
// submit name/email/TIN -> platform reviews -> verify emailed code + set password.
const CompanySignupPage = lazy(
  () => import('@/features/onboarding/company-signup/CompanySignupPage'),
);
const VerifyApplicationPage = lazy(
  () => import('@/features/onboarding/company-signup/VerifyApplicationPage'),
);

// Additional auth components
const ForgotPasswordPage = lazy(() => import('@/components/auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/components/auth/ResetPassword'));

// Auth components
const EmployeeSignup = lazy(() => import('@/features/auth/entry-points/components/EmployeeSignup'));
const TermsOfService = lazy(() => import('@/features/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/features/pages/PrivacyPolicy'));
// The three customer-facing URLs Intuit requires for production keys. Public,
// because QuickBooks links to them directly and a signed-out visitor must reach
// the sign-in page rather than a 404.
const QuickBooksLaunch = lazy(() =>
  import('@/features/pages/QuickBooksLanding').then((m) => ({ default: m.QuickBooksLaunch })));
const QuickBooksConnect = lazy(() =>
  import('@/features/pages/QuickBooksLanding').then((m) => ({ default: m.QuickBooksConnect })));
const QuickBooksDisconnected = lazy(() =>
  import('@/features/pages/QuickBooksLanding').then((m) => ({ default: m.QuickBooksDisconnected })));
const ImpersonationEntry = lazy(() => import('@/components/ImpersonationEntry'));

// Layouts
import { AuthLayout } from '@/features/shared/components/layout/AuthLayout';
import { TenantLayout } from '@/features/layouts/TenantLayout';

// Guards
import { AuthGuard } from '@/features/auth/route-protection/guards/AuthGuard';
import { PublicRoute } from '@/features/auth/route-protection/components/PublicRoute';

// Lazy loading wrapper.
//
// The boundary is safe at this level precisely because nothing edited is
// displaced: by the time a route's chunk fails, the previous page has
// already unmounted through navigation. Inside a page it would be wrong —
// see MyPayPage, where a failing tab must not take the page with it.
const LazyPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <LazySection fallback={() => <div className="p-6"><SectionFailed /></div>}>
    <Suspense fallback={<Splash />}>{children}</Suspense>
  </LazySection>
);

// Root redirect component
// Tenant-agnostic OAuth authorize entry (Agent Platform Phase 6).
import { OAuthAuthorizePage } from '@/features/pages/oauth/OAuthAuthorizePage';
import { AiApprovalEntryPage } from '@/features/pages/ai-approvals/AiApprovalEntryPage';
import { DeviceActivatePage } from '@/features/pages/oauth/DeviceActivatePage';
import { LazySection } from '@/features/shared/components/recovery/LazySection';
import { SectionFailed } from '@/features/shared/components/recovery/SectionFailed';

const RootRedirect: React.FC = () => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  
  // Check if user is authenticated
  const token = localStorage.getItem('auth_token');
  const user = localStorage.getItem('user');
  
  if (token && user) {
    try {
      const userData = JSON.parse(user);
      const tenantSlug = userData.tenantSlug || subdomain;

      // Main-app only supports employee and manager roles
      // Admin roles (tenant-admin, system-admin) should use the admin-portal

      if (tenantSlug && !['app', 'www', 'localhost'].includes(tenantSlug)) {
        return <Navigate to={`/dashboard/${tenantSlug}`} replace />;
      }

      return <Navigate to="/dashboard" replace />;
    } catch {
      // If parsing fails, go to login
    }
  }
  
  // Not authenticated -> the app always opens on login. (Marketing/landing
  // content lives in a separate advertisement app now.)
  return <Navigate to="/login" replace />;
};

// Create router
export const router = createBrowserRouter(
  [
    {
      path: '/',
      // Mounted at the root so every in-app navigation passes through it.
      // It is inert until something registers a hold, and it reads the same
      // shared registry the reload and update guards read — a form declares
      // its state once and is protected everywhere.
      element: (
        <>
          <NavigationGuard />
          <Outlet />
        </>
      ),
      errorElement: <RouteErrorFallback />,
      children: [
        {
          index: true,
          element: <RootRedirect />,
        },
        {
          path: 'test-header',
          element: (
            <LazyPage>
              <TestHeader />
            </LazyPage>
          ),
        },
        {
          // Operator impersonation handoff (token via URL hash from admin-portal)
          path: 'impersonate',
          element: (
            <LazyPage>
              <ImpersonationEntry />
            </LazyPage>
          ),
        },
        {
          // KaziPeople Sign — guarantor invite-token signing (public; the
          // emailed single-use token is the authorization).
          path: 'sign/:token',
          element: (
            <LazyPage>
              <GuarantorSignPage />
            </LazyPage>
          ),
        },
        {
          // Work Management — counterparty acknowledgement of completed work
          // (public; the single-use token in the URL is the authorization).
          path: 'ack/:token',
          element: (
            <LazyPage>
              <WorkAckPage />
            </LazyPage>
          ),
        },
        {
          // KaziPeople Sign — public document verification by certificate code.
          path: 'verify/:code',
          element: (
            <LazyPage>
              <VerifyDocumentPage />
            </LazyPage>
          ),
        },
        {
          // OAuth authorization entry (RFC 8414 authorization_endpoint) —
          // forwards to the signed-in user's tenant consent page.
          path: 'oauth/authorize',
          element: <OAuthAuthorizePage />,
        },
        {
          // AI approval link from MCP (v2.1 Phase 1c) — same forwarding: the
          // signed-in person lands on their own workspace's review page.
          path: 'ai-approval/:id',
          element: <AiApprovalEntryPage />,
        },
        {
          // Device-flow entry (RFC 8628 verification_uri) — same forwarding.
          path: 'activate',
          element: <DeviceActivatePage />,
        },
        {
          // Legal pages — publicly accessible to anyone (linked from auth + footers)
          path: 'terms',
          element: (
            <LazyPage>
              <TermsOfService />
            </LazyPage>
          ),
        },
        {
          path: 'privacy',
          element: (
            <LazyPage>
              <PrivacyPolicy />
            </LazyPage>
          ),
        },
        {
          // Intuit "Launch URL" — clicked from the app card inside QuickBooks.
          path: 'integrations/quickbooks/launch',
          element: (
            <LazyPage>
              <QuickBooksLaunch />
            </LazyPage>
          ),
        },
        {
          // Intuit "Connect/Reconnect URL".
          path: 'integrations/quickbooks/connect',
          element: (
            <LazyPage>
              <QuickBooksConnect />
            </LazyPage>
          ),
        },
        {
          // Intuit "Disconnect URL" — QuickBooks has already revoked by the
          // time anyone lands here; this explains it and offers the way back.
          path: 'integrations/quickbooks/disconnected',
          element: (
            <LazyPage>
              <QuickBooksDisconnected />
            </LazyPage>
          ),
        },
        {
          // Public routes
          element: <PublicRoute />,
          children: [
            {
              element: <AuthLayout />,
              children: [
                {
                  path: 'workspace',
                  element: (
                    <LazyPage>
                      <WorkspaceEntry />
                    </LazyPage>
                  ),
                },
                {
                  path: 'login',
                  element: (
                    <LazyPage>
                      <LoginPage />
                    </LazyPage>
                  ),
                },
                {
                  path: 'signin',
                  element: (
                    <LazyPage>
                      <LoginPage />
                    </LazyPage>
                  ),
                },
                {
                  path: 'register',
                  element: (
                    <LazyPage>
                      <InviteOnlySignup />
                    </LazyPage>
                  ),
                },
                {
                  path: 'signup',
                  element: (
                    <LazyPage>
                      <InviteOnlySignup />
                    </LazyPage>
                  ),
                },
                {
                  path: 'invite/:token',
                  element: (
                    <LazyPage>
                      <EmployeeSignup />
                    </LazyPage>
                  ),
                },
                {
                  path: 'forgot-password',
                  element: (
                    <LazyPage>
                      <ForgotPasswordPage />
                    </LazyPage>
                  ),
                },
                {
                  path: 'reset-password',
                  element: (
                    <LazyPage>
                      <ResetPasswordPage />
                    </LazyPage>
                  ),
                },
              ],
            },
          ],
        },
        {
          // Public gated company registration (no auth): apply, then verify the
          // emailed code + set a password to create the tenant.
          path: 'onboarding',
          element: <AuthLayout />,
          children: [
            {
              index: true,
              element: (
                <LazyPage>
                  <CompanySignupPage />
                </LazyPage>
              ),
            },
            {
              path: 'verify',
              element: (
                <LazyPage>
                  <VerifyApplicationPage />
                </LazyPage>
              ),
            },
          ],
        },
        {
          // Protected routes
          element: <AuthGuard />,
          children: [
            {
              path: 'dashboard/:tenantSlug/*',
              element: <TenantLayout />,
            },
            {
              path: 'dashboard/*',
              element: <TenantLayout />,
            },
            {
              path: 't/:tenantSlug/app/*',
              element: <TenantLayout />,
            },
            {
              path: 'app/*',
              element: <TenantLayout />,
            },
            // Add more protected routes here
          ],
        },
      ],
    },
    {
      // 404 fallback
      path: '*',
      element: <Navigate to="/" replace />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  }
);

// Export route paths as constants
export const ROUTES = {
  HOME: '/',
  WORKSPACE: '/workspace',
  LOGIN: '/login',
  SIGNIN: '/signin',
  REGISTER: '/register',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  INVITE: '/invite/:token',
  DASHBOARD: '/dashboard',
  ONBOARDING: '/onboarding',
  API_TEST: '/api-test',
} as const;

export type RoutePath = typeof ROUTES[keyof typeof ROUTES];
